/**
 * A component for collecting, uploading, and managing document files.
 * Supports multiple document types with file upload and deletion functionality.
 */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button, FileIcon, Flex, Text } from "@jsq/ds/core";
import { useFormContext, Form, FormCheckbox } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { DocumentFormValues } from "../../../hooks/documents/useDocumentProcessor";
import useUnsavedChangesTracker from "../../../hooks/documents/useUnsavedChangesTracker";
import { formatDate } from "../../../utils/date_formatter";
import { ButtonWrapper } from "../../common/ButtonWrapper";
import { DefaultModal } from "../../common/DefaultModal";
import { SingleFileUpload } from "../../common/SingleFileUpload";
import {
  deleteIcon,
  deleteIconContainer,
  uploaderInfoContainer,
  uploaderInfoIcon,
  uploaderInfoText,
} from "./style_vanilla.css";
import {
  DocumentCollectorProps,
  DocumentFormProps,
  DocumentData,
  DocumentInvitedUserInfo,
  DocumentModalTypeEnum,
} from "./types";
import { areDocumentsAvailable, prepareFilesToUpload } from "./utils";

// Supported file formats that can be uploaded through the document collector
const SUPPORTED_FORMATS = ["image/*", ".pdf", ".doc", ".docx", ".xlsx", ".xls", ".csv", ".zip"];

/**
 * Renders the form fields for document collection with file upload controls
 * for each document type specified.
 *
 * @param documentDetails - Array of document specifications to collect
 * @param isSubmitting - Whether the form is currently submitting
 * @param onNavigateBack - Optional callback for navigation to previous screen
 */
const DocumentsForm: React.FC<{
  documentDetails: DocumentFormProps[];
  isSubmitting: boolean;
  submitFormButton?: string;
  showNoDocumentsTT?: boolean;
  allowSkipToUpload?: boolean;
  onNavigateBack?: () => void;
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
  onAllDocumentsReadyChange?: (allReady: boolean) => void;
  initialFiles: DocumentData[];
}> = ({
  documentDetails,
  isSubmitting,
  submitFormButton,
  showNoDocumentsTT,
  allowSkipToUpload,
  onNavigateBack,
  onUnsavedChangesChange,
  onAllDocumentsReadyChange,
  initialFiles,
}) => {
  const [documentOwner, setDocumentOwner] = useState<
    (DocumentInvitedUserInfo & { index: number }) | null
  >(null);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [hasAttachedDocuments, setHasAttachedDocuments] = useState(false);
  const formContext = useFormContext<DocumentFormValues>();

  const showGuestInfo = useCallback(
    (data: DocumentFormProps, index: number) => {
      const currentUploaderInfo = formContext.values.documents[index]?.uploaderInfo;
      return data.uploaderInfo && data.id && currentUploaderInfo != null;
    },
    [formContext.values.documents],
  );

  const disableSubmit = useMemo(() => {
    if (formContext.values.skipDocumentUpload) {
      return false;
    }

    return showNoDocumentsTT && !enableSubmit;
  }, [showNoDocumentsTT, enableSubmit, formContext.values.skipDocumentUpload]);

  useEffect(() => {
    const hasNewFiles = formContext.values.documents.some((document) => {
      return document.file != null;
    });
    setEnableSubmit(hasNewFiles);
    setHasAttachedDocuments(hasNewFiles);

    if (onAllDocumentsReadyChange) {
      onAllDocumentsReadyChange(areDocumentsAvailable(formContext.values.documents));
    }
  }, [formContext.values.documents, onAllDocumentsReadyChange]);

  // Track unsaved changes and notify parent component
  useUnsavedChangesTracker(initialFiles, onUnsavedChangesChange);
  return (
    <Flex flexDirection="column" gap={Sizes.XS}>
      {allowSkipToUpload && (
        <Flex justifyContent="flex-start">
          <FormCheckbox
            name="skipDocumentUpload"
            label="I cannot provide any of the documents listed below"
            disabled={hasAttachedDocuments}
          />
        </Flex>
      )}
      {documentDetails.map((data, index) => (
        <Flex key={index} flexDirection="column">
          <SingleFileUpload
            title={data.title}
            description={data.description}
            initialFile={data.file}
            acceptedFileFormats={SUPPORTED_FORMATS}
            isDisabled={showGuestInfo(data, index) || formContext.values.skipDocumentUpload}
            onFileSelected={(file) => {
              void formContext.setFieldValue(`documents[${index}].file`, file);
              return;
            }}
            onFileDeleted={() => {
              void formContext.setFieldValue(`documents[${index}].file`, null);
              return;
            }}
          />
          {showGuestInfo(data, index) && (
            <Flex className={uploaderInfoContainer}>
              <Flex className={uploaderInfoIcon}>
                <FileIcon extension="" size="x-large" />
              </Flex>
              <Text variant="regular-md" className={uploaderInfoText}>
                Uploaded by {data.uploaderInfo}
              </Text>
              <Flex className={deleteIconContainer}>
                <Icon
                  data-testid={`delete-icon-${data.id}`}
                  type="trash"
                  size="large"
                  className={deleteIcon}
                  color={Colors.Neutral900}
                  onClick={() => {
                    setDocumentOwner({
                      id: data.id!,
                      emailAddress: data.uploaderInfo!,
                      index: index,
                      uploadDate: data.lastUploadedDate,
                    });
                  }}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      ))}
      <DefaultModal
        show={!!documentOwner}
        modalSize="small"
        title="Warning"
        body={
          <Flex flexDirection="column" gap={Sizes.MD}>
            <Text>
              This document was uploaded on {formatDate(documentOwner?.uploadDate)} by{" "}
              {documentOwner?.emailAddress}.
            </Text>
            <Text>Are you sure you want to delete it?</Text>
          </Flex>
        }
        negBtnText="Cancel"
        posBtnText="Delete"
        posBtnVariant="danger"
        onClose={() => {
          setDocumentOwner(null);
        }}
        onConfirm={() => {
          if (documentOwner) {
            void formContext.setFieldValue(
              `documents[${documentOwner.index}].uploaderInfo`,
              null,
            );
            setDocumentOwner(null);
          }
        }}
      />
      <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
        {onNavigateBack && (
          <Button variant="neutral" onClick={onNavigateBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        <ButtonWrapper
          tooltip={"At least one document must be uploaded in order to submit"}
          disabled={disableSubmit}
        >
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={disableSubmit}
            data-testid="complete-button"
          >
            {submitFormButton || "Complete"}
          </Button>
        </ButtonWrapper>
      </Flex>
    </Flex>
  );
};

/**
 * Main component for collecting document files with support for uploading, replacing,
 * and deleting documents. Tracks which files are new, which are replacements, and
 * which have been removed.
 *
 * @param documentDetails - Array of document details to collect
 * @param onNavigateBack - Optional callback for handling navigation to previous screen
 * @param onComplete - Callback for handling form submission with files to delete and upload
 */
export const DocumentCollector: React.FC<DocumentCollectorProps> = ({
  documentDetails,
  isSubmitting,
  buttonConfig,
  allowSkipToUpload = false,
  onNavigateBack,
  onComplete,
  onUnsavedChangesChange,
  onAllDocumentsReadyChange,
  preUploadCallback,
  inviterInfo,
}) => {
  const [documentFormValues, setDocumentFormValues] = useState<
    DocumentFormValues | undefined
  >();
  const [modalType, setModalType] = useState<DocumentModalTypeEnum | undefined>();

  // Convert DocumentFormProps to DocumentData (extract only essential properties)
  const initialFiles = useMemo(() => {
    return documentDetails.map((detail) => ({
      id: detail.id,
      file: detail.file,
      baseType: detail.baseType,
      subType: detail.subType,
      uploaderInfo: detail.uploaderInfo,
    }));
  }, [documentDetails]);

  const uploadHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      const { newFiles, deleteDocIds } = prepareFilesToUpload(newSelectedFiles);
      onComplete(newFiles, deleteDocIds);
    },
    [onComplete],
  );

  const submitHandler = useCallback(
    (newSelectedFiles: DocumentFormValues) => {
      if (newSelectedFiles.skipDocumentUpload) {
        // User explicitly said documents are not available - show confirmation modal
        setModalType(DocumentModalTypeEnum.NO_DOCS_AVAILABLE);
        setDocumentFormValues(newSelectedFiles);
        return;
      }

      const hasDocumentsNotAvailable = !areDocumentsAvailable(newSelectedFiles.documents);
      if (hasDocumentsNotAvailable && inviterInfo) {
        // For guest document collection alone we need to show the missing documents modal and get confirmation from the user
        setModalType(DocumentModalTypeEnum.GUEST_MISSING_DOCS);
        setDocumentFormValues(newSelectedFiles);
        return;
      }
      if (preUploadCallback && !hasDocumentsNotAvailable) {
        preUploadCallback();
      }
      uploadHandler(newSelectedFiles);
    },
    [uploadHandler, preUploadCallback, inviterInfo],
  );

  const getModalContent = () => {
    if (modalType === DocumentModalTypeEnum.NO_DOCS_AVAILABLE) {
      return {
        body: "You have indicated that you cannot provide any of the requested documents. Are you sure you want to submit without uploading any documents?",
        title: "Warning",
        posBtnText: "Submit",
      };
    }

    return {
      body: `You have not uploaded all the documents requested by ${inviterInfo?.name} (${inviterInfo?.email}). Documents you have uploaded may be submitted, but you will not be able to upload additional documents.`,
      title: "Warning",
      posBtnText: "Submit",
    };
  };

  const modalContent = getModalContent();

  return (
    <div>
      <Form<DocumentFormValues>
        initialValues={{
          documents: initialFiles,
          skipDocumentUpload: false,
        }}
        submitOnlyChangedValues={false}
        onSubmit={submitHandler}
      >
        <DocumentsForm
          documentDetails={documentDetails}
          isSubmitting={isSubmitting}
          submitFormButton={buttonConfig?.label}
          showNoDocumentsTT={buttonConfig?.showNoDocumentsTT}
          allowSkipToUpload={allowSkipToUpload}
          onNavigateBack={onNavigateBack}
          onUnsavedChangesChange={onUnsavedChangesChange}
          onAllDocumentsReadyChange={onAllDocumentsReadyChange}
          initialFiles={initialFiles}
        />
      </Form>

      <DefaultModal
        show={!!modalType}
        modalSize="medium"
        title={modalContent.title}
        body={modalContent.body}
        negBtnText="Cancel"
        posBtnText={modalContent.posBtnText}
        onClose={() => {
          setModalType(undefined);
        }}
        onConfirm={() => {
          uploadHandler(documentFormValues!);
          setModalType(undefined);
        }}
      />
    </div>
  );
};
